import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getNotificationChannelLabel, Duty, StaffRule, Store, Shift, Position, Staff, DailyEvent, ShiftPattern, getJSTDate, getJSTDateString, minutesToTime, timeToMinutes } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { DialogType } from '../components/ui/Dialog';
import { sMin, eMin, isoAt, isoRange } from '../utils/shiftTime';
import { CoverageGap, DEFAULT_MIN_SHIFT_MINUTES, vacancyGaps, maxMissing, missingMinutes, requiredMinutes, gapsLabel, gapOverlap, fitToGaps, allocateToVacancies } from '../utils/coverage';

export interface CreateDragState {
  isDragging: boolean;
  positionId: string | null;
  startSlot: number | null;
  currentSlot: number | null;
}

export interface EditDragState {
  mode: 'move' | 'resize-start' | 'resize-end' | null;
  targetId: string | null;
  initialStartMinutes: number;
  initialEndMinutes: number;
  initialSlot: number;
  currentSlot: number;
  offsetSlots: number;
}

export interface DialogState {
  isOpen: boolean;
  type: DialogType;
  title: string;
  message: string;
  onConfirm: () => void;
}

const INITIAL_CREATE_DRAG: CreateDragState = { isDragging: false, positionId: null, startSlot: null, currentSlot: null };
const INITIAL_EDIT_DRAG: EditDragState = { mode: null, targetId: null, initialStartMinutes: 0, initialEndMinutes: 0, initialSlot: 0, currentSlot: 0, offsetSlots: 0 };
const INITIAL_DIALOG: DialogState = { isOpen: false, type: 'alert', title: '', message: '', onConfirm: () => {} };

export const useShiftCreation = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [currentDate, setCurrentDate] = useState(getJSTDate());
  const [stores, setStores] = useState<Store[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [duties, setDuties] = useState<Duty[]>([]);
  const [staffRules, setStaffRules] = useState<StaffRule[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [allShiftsRaw, setAllShiftsRaw] = useState<Shift[]>([]);
  const [vacancies, setVacancies] = useState<Shift[]>([]);
  const [requests, setRequests] = useState<Shift[]>([]);
  const [assignedShifts, setAssignedShifts] = useState<Shift[]>([]);
  const [events, setEvents] = useState<DailyEvent[]>([]);
  const [shiftPatterns, setShiftPatterns] = useState<ShiftPattern[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetPeriod, setTargetPeriod] = useState<{start: Date, end: Date} | null>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [setupStartDate, setSetupStartDate] = useState('');
  const [setupEndDate, setSetupEndDate] = useState('');

  const [createDrag, setCreateDrag] = useState<CreateDragState>(INITIAL_CREATE_DRAG);
  const [editDrag, setEditDrag] = useState<EditDragState>(INITIAL_EDIT_DRAG);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState<Partial<Shift>>({});
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [selectedVacancyForCandidates, setSelectedVacancyForCandidates] = useState<Shift | null>(null);

  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Partial<DailyEvent>>({});
  const [isEventSaving, setIsEventSaving] = useState(false);

  const [isSavePatternModalOpen, setIsSavePatternModalOpen] = useState(false);
  const [newPatternName, setNewPatternName] = useState('');

  const [dialogState, setDialogState] = useState<DialogState>(INITIAL_DIALOG);
  // 自動作成の結果（不足枠と、毎日必要な業務の対応者がいない日）
  const [isAutoResultOpen, setIsAutoResultOpen] = useState(false);
  const [offeringKey, setOfferingKey] = useState<string | null>(null);
  const [autoResult, setAutoResult] = useState<null | {
    assignedCount: number; totalNeed: number; filled: number;
    shortages: Array<{ v: Shift; missing: number; gaps: CoverageGap[] }>;
    dutyMissing: Array<{ day: string; duty: Duty }>;
  }>(null);

  const todayStr = useMemo(() => getJSTDateString(new Date()), []);
  const activeStore = stores.find(s => s.id === selectedStoreId);

  // ページ離脱防止
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '作成途中のシフトがありますが離れますか？';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // シフトデータの分類
  const categorizeShifts = useCallback((allShifts: Shift[], date: Date, storeId: string) => {
    const dateStr = getJSTDateString(date);
    const dayShifts = allShifts.filter(s => s.store === storeId && s.start.startsWith(dateStr));
    setVacancies(dayShifts.filter(s => s.type === 'vacancy'));
    setRequests(dayShifts.filter(s => s.type === 'request' && !s.confirmed));
    setAssignedShifts(dayShifts.filter(s => (s.type === 'fixed' || (s.type === 'request' && s.confirmed))));
  }, []);

  // モックDBのリアルタイム購読（元はFirestore onSnapshot）
  useEffect(() => {
    if (!selectedStoreId || !targetPeriod) return;
    const start = getJSTDateString(new Date(targetPeriod.start.getTime() - 86400000));
    const end = getJSTDateString(new Date(targetPeriod.end.getTime() + 86400000));
    const unsubscribe = api.subscribeStoreShifts(selectedStoreId, { start, end }, (shifts) => {
      setAllShiftsRaw(shifts);
      categorizeShifts(shifts, currentDate, selectedStoreId);
    });
    return () => unsubscribe();
  }, [selectedStoreId, targetPeriod?.start.getTime(), currentDate, categorizeShifts]);

  // 全店舗のシフト（他店舗での勤務も、同日重複・勤務時間・連勤の判定に含める）
  const [allStoresShifts, setAllStoresShifts] = useState<Shift[]>([]);
  useEffect(() => {
    if (!targetPeriod) return;
    const start = getJSTDateString(new Date(targetPeriod.start.getTime() - 7 * 86400000));
    const end = getJSTDateString(new Date(targetPeriod.end.getTime() + 86400000));
    return api.subscribeAllShifts({ start, end }, setAllStoresShifts);
  }, [targetPeriod?.start.getTime(), targetPeriod?.end.getTime()]);

  const loadInitialData = useCallback(async (storeId: string) => {
    try {
      const [loadedEvents, loadedPatterns] = await Promise.all([
        api.getEvents(storeId),
        api.getShiftPatterns(storeId)
      ]);
      setEvents(loadedEvents || []);
      setShiftPatterns(loadedPatterns || []);
    } catch(e) { console.error(e); }
  }, []);

  // ---- 計画ステップでのポジション追加・削除 ----
  const addPosition = async (name: string, color: string): Promise<boolean> => {
    const trimmed = name.trim();
    if (!trimmed || !selectedStoreId) return false;
    const storePositions = positions.filter(p => p.storeIds.includes(selectedStoreId));
    if (storePositions.some(p => p.name === trimmed)) { showToast('同じ名前のポジションがあります。', 'error'); return false; }
    // 他店舗に同名のポジションがあれば、それをこの店舗でも使う。
    const shared = positions.find(p => p.name === trimmed);
    const target: Position = shared ? { ...shared, storeIds: [...shared.storeIds, selectedStoreId] } : { id: `pos_${Date.now()}`, name: trimmed, color, storeIds: [selectedStoreId] };
    try {
      await api.savePositions([target]);
      setPositions(prev => shared ? prev.map(p => p.id === target.id ? target : p) : [...prev, target]);
      showToast(`ポジション「${trimmed}」を追加しました。`, 'success');
      return true;
    } catch (e) { showToast('ポジションの追加に失敗しました。', 'error'); return false; }
  };

  const removePosition = (pos: Position) => {
    const periodVacancyCount = targetPeriod ? allShiftsRaw.filter(sh => sh.store === selectedStoreId && sh.type === 'vacancy' && sh.positionId === pos.id).length : 0;
    setDialogState({
      isOpen: true, type: 'confirm', title: 'ポジションの削除',
      message: `「${pos.name}」をこの店舗から削除します。${periodVacancyCount > 0 ? `\n作成期間中のこのポジションの募集枠（${periodVacancyCount}件）も削除します。` : ''}\nモデルシフトからも外れます。よろしいですか？`,
      onConfirm: async () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        setIsRefreshing(true);
        try {
          const ids = allShiftsRaw.filter(sh => sh.store === selectedStoreId && sh.type === 'vacancy' && sh.positionId === pos.id).map(sh => sh.id);
          await Promise.all(ids.map(id => api.deleteShift(id)));
          const otherStores = pos.storeIds.filter(id => id !== selectedStoreId);
          if (otherStores.length > 0) await api.savePositions([{ ...pos, storeIds: otherStores }]);
          else await api.deletePosition(pos.id);
          const patterns = (await api.getShiftPatterns(selectedStoreId)).filter(pt => pt.items.some(it => it.positionId === pos.id));
          await Promise.all(patterns.map(pt => api.saveShiftPattern({ ...pt, items: pt.items.filter(it => it.positionId !== pos.id) })));
          setShiftPatterns(prev => prev.map(pt => ({ ...pt, items: pt.items.filter(it => it.positionId !== pos.id) })));
          setPositions(prev => otherStores.length > 0 ? prev.map(p => p.id === pos.id ? { ...p, storeIds: otherStores } : p) : prev.filter(p => p.id !== pos.id));
          showToast(`ポジション「${pos.name}」を削除しました。`, 'success');
        } catch (e) { showToast('ポジションの削除に失敗しました。', 'error'); }
        finally { setIsRefreshing(false); }
      }
    });
  };

  // 対象店舗の切り替え（期間・進行状況・募集パターンを店舗ごとに読み込む）。
  const applyStore = useCallback(async (store: Store) => {
    setSelectedStoreId(store.id);
    setIsDirty(false);
    await loadInitialData(store.id);
    if (store.lastShiftPeriod) {
      const [sy, sm, sd] = store.lastShiftPeriod.start.split('-').map(Number);
      const [ey, em, ed] = store.lastShiftPeriod.end.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd);
      const end = new Date(ey, em - 1, ed);
      setTargetPeriod({ start, end });
      setCurrentDate(start);
      const stepByStatus = { planning: 0, requesting: 1, adjusting: 2, published: 3 } as const;
      setCurrentStep(store.shiftCreationStatus ? stepByStatus[store.shiftCreationStatus] : 0);
      setIsPeriodModalOpen(false);
    } else {
      setTargetPeriod(null);
      setCurrentStep(0);
      setIsPeriodModalOpen(true);
    }
  }, [loadInitialData]);

  const handleChangeStore = async (storeId: string) => {
    if (storeId === selectedStoreId) return;
    // 変更はその場で保存されるので、店舗を切り替えても失われない
    const latest = (await api.getStores()).find(st => st.id === storeId);
    if (latest) {
      setStores(prev => prev.map(st => st.id === latest.id ? latest : st));
      await applyStore(latest);
    }
  };

  // 初期データロード
  useEffect(() => {
    if (!currentUser) return;
    const init = async () => {
      setIsLoading(true);
      try {
        const [loadedStores, loadedPositions, loadedStaff] = await Promise.all([api.getStores(), api.getPositions(), api.getStaff()]);
        setStores(loadedStores || []);
        setPositions(loadedPositions || []);
        setAllStaff(loadedStaff || []);
        setDuties(await api.getDuties());
        setStaffRules(await api.getStaffRules());
        if (loadedStores.length > 0) {
          await applyStore(loadedStores[0]);
        }
      } catch(e) { console.error(e); }
      finally { setIsLoading(false); }
    };
    init();
  }, [currentUser]);

  // 未設定日数の算出
  const unconfiguredDaysCount = useMemo(() => {
    if (!targetPeriod) return 0;
    let count = 0;
    const start = new Date(targetPeriod.start);
    const end = new Date(targetPeriod.end);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dStr = getJSTDateString(d);
      const isHoliday = events.some(e => e.date === dStr && e.title === '休日');
      if (isHoliday) continue;
      const hasShifts = allShiftsRaw.some(s => s.store === selectedStoreId && s.start.startsWith(dStr));
      if (!hasShifts) count++;
    }
    return count;
  }, [targetPeriod, events, allShiftsRaw, selectedStoreId]);

  // 今日が休日かどうか
  const isTodayHoliday = useMemo(() => {
    const dateStr = getJSTDateString(currentDate);
    return events.some(e => e.date === dateStr && e.title === '休日');
  }, [events, currentDate]);

  const isReadyForRequest = useMemo(() => unconfiguredDaysCount === 0, [unconfiguredDaysCount]);

  // 進行状況を店舗に保存する（全店舗状況ビューと、再読み込み後の復元に使う）。
  const persistStatus = useCallback(async (status: Store['shiftCreationStatus']) => {
    if (!activeStore) return;
    const updated = { ...activeStore, shiftCreationStatus: status };
    try {
      await api.saveStores([updated]);
      setStores(prev => prev.map(st => st.id === updated.id ? updated : st));
    } catch (e) { console.error(e); }
  }, [activeStore]);

  // ステップ移動（計画→募集→調整→公開）。前のステップへは自由に戻れ、先へは順に進む。
  const goToStep = async (step: number) => {
    if (step === currentStep) return;
    if (step > currentStep + 1) { showToast('前のステップから順に進めてください。', 'info'); return; }
    if (step === 1 && currentStep === 0) { showToast('「シフト回答依頼」を押すと募集ステップに進みます。', 'info'); return; }
    setCurrentStep(step);
    // 「公開」ステップは確定・通知を実行するまで「調整中」として保存する。
    const statusByStep: Store['shiftCreationStatus'][] = ['planning', 'requesting', 'adjusting', 'adjusting'];
    await persistStatus(statusByStep[step]);
    // 募集から調整に進んだとき、まだ割り当てがなければ、回収した希望をもとに自動で組む。
    if (step === 2 && currentStep === 1 && !periodAssigned.some(sh => sh.type !== 'fixed')) await runAutoAssign();
  };

  // ---- 期間全体の分析（割当・警告・未提出者） ----
  const periodDays = useMemo(() => {
    if (!targetPeriod) return [] as string[];
    const out: string[] = [];
    for (let d = new Date(targetPeriod.start); d <= targetPeriod.end; d.setDate(d.getDate() + 1)) out.push(getJSTDateString(d));
    return out;
  }, [targetPeriod]);

  const storeStaff = useMemo(() => allStaff.filter(st => st.stores?.includes(selectedStoreId) && st.status !== 'resigned' && st.status !== 'inactive'), [allStaff, selectedStoreId]);

  const hoursOf = (sh: Shift) => {
    return (eMin(sh) - sMin(sh)) / 60;
  };
  const isAssigned = (sh: Shift) => !!sh.staffId && sh.type !== 'vacancy' && sh.type !== 'off' && (sh.type === 'fixed' || sh.confirmed === true);

  // 期間内で割り当て済みのシフト（固定＋確定済み希望）
  const periodAssigned = useMemo(() => allShiftsRaw.filter(sh => sh.store === selectedStoreId && isAssigned(sh) && periodDays.includes(sh.start.split('T')[0])), [allShiftsRaw, selectedStoreId, periodDays]);
  // 全店舗の割り当て済みシフト（期間内）
  const periodAssignedAll = useMemo(() => allStoresShifts.filter(sh => isAssigned(sh) && periodDays.includes(sh.start.split('T')[0])), [allStoresShifts, periodDays]);
  // 有効な応募者数（応募後に同じ日のどこかへ割り当て済みになった人は除く）
  const activeApplicantCount = useCallback((v: Shift) => (v.applicants || []).filter(id => !periodAssignedAll.some(a => a.staffId === id && a.start.startsWith(v.start.slice(0, 10)))).length, [periodAssignedAll]);

  const periodVacancies = useMemo(() => allShiftsRaw.filter(sh => sh.store === selectedStoreId && sh.type === 'vacancy' && periodDays.includes(sh.start.split('T')[0])), [allShiftsRaw, selectedStoreId, periodDays]);
  const periodRequests = useMemo(() => allShiftsRaw.filter(sh => sh.store === selectedStoreId && (sh.type === 'request' || sh.type === 'off') && periodDays.includes(sh.start.split('T')[0])), [allShiftsRaw, selectedStoreId, periodDays]);

  // 募集枠に割り当てられているシフト（同日・同ポジションで時間が重なるもの）
  const assignedForVacancy = useCallback((v: Shift, assigned: Shift[] = periodAssigned) => {
    const day = v.start.split('T')[0];
    const vs = sMin(v); const ve = eMin(v);
    if (v.label === '欠員') return assigned.filter(a => a.vacancyId === v.id);
    // 重なる募集枠が複数ある場合は、どの枠の人かを振り分けて返す（同じ人を2つの枠に数えない）
    const group = periodVacancies.filter(x => x.label !== '欠員' && x.store === v.store && x.positionId === v.positionId && x.start.slice(0, 10) === v.start.slice(0, 10));
    if (group.length > 1) return (allocateToVacancies(group, assigned.filter(a => a.store === v.store && a.positionId === v.positionId && a.start.slice(0, 10) === v.start.slice(0, 10))).get(v.id) || []);
    return assigned.filter(a => a.store === v.store && a.start.split('T')[0] === day && a.positionId === v.positionId && sMin(a) < ve && eMin(a) > vs);
  }, [periodAssigned, periodVacancies]);

  // 募集枠の不足区間（時間帯ごとに、必要人数に届いていない時間と人数）
  const gapsOf = useCallback((v: Shift, assigned: Shift[] = periodAssigned): CoverageGap[] => vacancyGaps(v, periodVacancies, assigned), [periodVacancies, periodAssigned]);
  const minShift = activeStore?.minShiftMinutes || DEFAULT_MIN_SHIFT_MINUTES;

  const staffHoursInPeriod = useCallback((staffId: string, assigned: Shift[] = periodAssignedAll) => assigned.filter(a => a.staffId === staffId).reduce((acc, a) => acc + hoursOf(a), 0), [periodAssignedAll]);

  const maxConsecutiveDays = (staffId: string, assigned: Shift[]) => {
    const days = Array.from(new Set(assigned.filter(a => a.staffId === staffId).map(a => a.start.split('T')[0]))).sort();
    let best = 0, run = 0, prev: string | null = null;
    days.forEach(d => {
      if (prev) { const diff = (new Date(d).getTime() - new Date(prev).getTime()) / 86400000; run = diff === 1 ? run + 1 : 1; } else run = 1;
      best = Math.max(best, run); prev = d;
    });
    return best;
  };

  // 未提出者：期間内に希望（出勤・休み）が1件もないスタッフ
  const unsubmittedStaff = useMemo(() => storeStaff.filter(st => st.role !== 'store_manager' && st.role !== 'system_admin' && !periodRequests.some(r => r.staffId === st.id) && !periodAssigned.some(a => a.staffId === st.id && a.type === 'fixed')), [storeStaff, periodRequests, periodAssigned]);

  // ---- 対応業務（鍵の開け閉めなど） ----
  const storeDuties = useMemo(() => duties.filter(d => d.storeIds.includes(selectedStoreId)), [duties, selectedStoreId]);
  const staffHasDuty = useCallback((staffId: string | null, dutyId: string) => !!staffId && (allStaff.find(st => st.id === staffId)?.dutyIds || []).includes(dutyId), [allStaff]);
  // 指定日に割り当て済みのスタッフのうち、その業務に対応できる人
  const dutyHoldersOn = useCallback((day: string, dutyId: string, assigned: Shift[] = periodAssigned) => {
    const ids = Array.from(new Set(assigned.filter(a => a.store === selectedStoreId && a.start.startsWith(day) && staffHasDuty(a.staffId, dutyId)).map(a => a.staffId as string)));
    return ids.map(id => allStaff.find(st => st.id === id)).filter((x): x is Staff => !!x);
  }, [periodAssigned, staffHasDuty, allStaff, selectedStoreId]);
  // 表示中の日の対応業務の状況
  const dayDutyStatus = useMemo(() => {
    const day = getJSTDateString(currentDate);
    const assignedToday = allShiftsRaw.filter(sh => sh.store === selectedStoreId && sh.start.startsWith(day) && isAssigned(sh));
    return storeDuties.map(d => ({ duty: d, holders: dutyHoldersOn(day, d.id, assignedToday) }));
  }, [currentDate, allShiftsRaw, selectedStoreId, storeDuties, dutyHoldersOn]);

  // ---- ペア・NG・対応業務の同時間帯上限 ----
  const storeRules = useMemo(() => staffRules.filter(r => r.storeIds.includes(selectedStoreId)), [staffRules, selectedStoreId]);
    const nameOf = useCallback((id: string) => { const st = allStaff.find(x => x.id === id); return st ? `${st.lastName}さん` : ''; }, [allStaff]);
  // 指定の時間帯に、NGの相手が割り当てられているか（相手の名前を返す）
  const ngConflicts = useCallback((staffId: string, day: string, s0: number, e0: number, assigned: Shift[]) =>
    storeRules.filter(r => r.type === 'ng' && (r.staffId === staffId || r.partnerId === staffId))
      .map(r => r.staffId === staffId ? r.partnerId : r.staffId)
      .filter(other => assigned.some(a => a.staffId === other && a.start.startsWith(day) && sMin(a) < e0 && eMin(a) > s0))
      .map(nameOf), [storeRules, nameOf]);
  // ペアの相手（このスタッフが入るなら一緒に入れる人）が、その時間帯に割り当て済みか
  const pairPartnersOf = useCallback((staffId: string) => storeRules.filter(r => r.type === 'pair' && r.staffId === staffId).map(r => r.partnerId), [storeRules]);
  const partnerPresent = useCallback((partnerId: string, day: string, s0: number, e0: number, assigned: Shift[]) =>
    assigned.some(a => a.staffId === partnerId && a.store === selectedStoreId && a.start.startsWith(day) && sMin(a) < e0 && eMin(a) > s0), [selectedStoreId]);
  // 対応業務の同時間帯の人数の最大値
  const dutyPeak = useCallback((dutyId: string, day: string, assigned: Shift[]) => {
    const iv = assigned.filter(a => a.store === selectedStoreId && a.start.startsWith(day) && staffHasDuty(a.staffId, dutyId)).map(a => [sMin(a), eMin(a)] as const);
    let peak = 0;
    iv.forEach(([s0]) => { peak = Math.max(peak, iv.filter(([a0, b0]) => a0 <= s0 && b0 > s0).length); });
    return peak;
  }, [selectedStoreId, staffHasDuty]);

  // 警告一覧
  const warnings = useMemo(() => {
    const out: Array<{ kind: 'shortage' | 'duty_missing' | 'ng_conflict' | 'pair_missing' | 'duty_over' | 'over_limit' | 'consecutive' | 'no_expert'; date?: string; message: string }> = [];
    const mdOf = (day: string) => day.slice(5).replace('-', '/');
    // NG：同じ時間帯に入っている
    storeRules.filter(r => r.type === 'ng').forEach(r => {
      periodAssigned.filter(a => a.staffId === r.staffId).forEach(a => {
        const day = a.start.slice(0, 10);
        if (ngConflicts(r.staffId, day, sMin(a), eMin(a), periodAssigned.filter(x => x.staffId === r.partnerId)).length > 0) out.push({ kind: 'ng_conflict', date: day, message: `${mdOf(day)} ${nameOf(r.staffId)}と${nameOf(r.partnerId)}が同じ時間帯に入っています（NG）。` });
      });
    });
    // ペア：一緒に入るはずの相手がいない
    storeRules.filter(r => r.type === 'pair').forEach(r => {
      periodAssigned.filter(a => a.staffId === r.staffId).forEach(a => {
        const day = a.start.slice(0, 10);
        if (!partnerPresent(r.partnerId, day, sMin(a), eMin(a), periodAssigned)) out.push({ kind: 'pair_missing', date: day, message: `${mdOf(day)} ${nameOf(r.staffId)}のペアの${nameOf(r.partnerId)}が同じ時間帯にいません。` });
      });
    });
    // 対応業務の同時間帯の上限超え
    const assignedDays = Array.from(new Set(periodAssigned.map(a => a.start.slice(0, 10))));
    storeDuties.filter(d => d.maxConcurrent).forEach(d => assignedDays.forEach(day => {
      const peak = dutyPeak(d.id, day, periodAssigned);
      if (peak > (d.maxConcurrent || 0)) out.push({ kind: 'duty_over', date: day, message: `${mdOf(day)} 「${d.name}」の対応者が同じ時間帯に${peak}名います（上限${d.maxConcurrent}名）。` });
    }));
    // 毎日必要な対応業務：募集枠のある日（営業日）に、対応できるスタッフが割り当てられているか
    const businessDays = Array.from(new Set(periodVacancies.map(v => v.start.split('T')[0])));
    storeDuties.filter(d => d.requiredDaily).forEach(d => {
      businessDays.forEach(day => {
        if (dutyHoldersOn(day, d.id).length === 0) out.push({ kind: 'duty_missing', date: day, message: `${day.slice(5).replace('-', '/')} 「${d.name}」に対応できるスタッフがいません。` });
      });
    });
    periodVacancies.forEach(v => {
      const assigned = assignedForVacancy(v);
      const pos = positions.find(p => p.id === v.positionId);
      const day = v.start.split('T')[0];
      const gaps = gapsOf(v);
      if (gaps.length > 0) out.push({ kind: 'shortage', date: day, message: `${day.slice(5).replace('-', '/')} ${pos?.name || ''}：${gapsLabel(gaps)}不足。${v.openCall ? `追加募集中、応募${activeApplicantCount(v)}名。` : ''}` });
      if (assigned.length > 0 && !assigned.some(a => (allStaff.find(st => st.id === a.staffId)?.skills || []).some(sk => sk.positionId === v.positionId && sk.level >= 3))) {
        out.push({ kind: 'no_expert', date: day, message: `${day.slice(5).replace('-', '/')} ${pos?.name || ''}：経験者（レベル3以上）が不在。` });
      }
    });
    // この店舗のスタッフと、この店舗にヘルプで入るスタッフ（勤務時間・連勤は全店舗の合計で判定）
    const targetStaff = [...storeStaff, ...allStaff.filter(st => !storeStaff.some(x => x.id === st.id) && periodAssigned.some(a => a.staffId === st.id))];
    targetStaff.forEach(st => {
      const hours = staffHoursInPeriod(st.id);
      if (st.monthlyWorkHourLimit && hours > st.monthlyWorkHourLimit) out.push({ kind: 'over_limit', message: `${st.lastName} ${st.firstName}：月間上限${st.monthlyWorkHourLimit}時間に対し${hours.toFixed(1)}時間。` });
      const run = maxConsecutiveDays(st.id, periodAssignedAll);
      if (run >= 6) out.push({ kind: 'consecutive', message: `${st.lastName} ${st.firstName}：${run}連勤。` });
    });
    const order = { shortage: 0, duty_missing: 1, ng_conflict: 2, pair_missing: 3, duty_over: 4, over_limit: 5, consecutive: 6, no_expert: 7 };
    return out.sort((a, b) => order[a.kind] - order[b.kind] || (a.date || '').localeCompare(b.date || ''));
  }, [periodVacancies, periodAssigned, periodAssignedAll, storeStaff, positions, allStaff, assignedForVacancy, gapsOf, staffHoursInPeriod, storeDuties, dutyHoldersOn, activeApplicantCount, storeRules, ngConflicts, partnerPresent, dutyPeak, nameOf]);

  // 候補：募集枠の日に希望を出しているスタッフ（適合条件と理由付き）
  // 候補の種類
  //  assigned：この枠に割り当て済み／request：希望あり／applicant：追加募集・打診に応募／
  //  available：希望はないが入れる（打診の対象）／help：他店舗のスタッフ（ヘルプの打診）／ng：条件に合わない
  type CandidateKind = 'assigned' | 'request' | 'applicant' | 'available' | 'help' | 'ng';
  const candidatesFor = useCallback((v: Shift) => {
    const day = v.start.split('T')[0];
    const vs = sMin(v); const ve = eMin(v);
    const slotHours = Math.max(0, ve - vs) / 60;
    const alreadyAssigned = assignedForVacancy(v);
    const gaps = gapsOf(v);
    // 希望がない人（打診・ヘルプ・応募）は、不足している時間に入ってもらう
    const gapSpan = gaps.length > 0 ? fitToGaps(vs, ve, gaps, minShift) : { start: vs, end: ve };
    const PRI = { high: 3, medium: 2, low: 1 } as const;
    const ownIds = new Set(storeStaff.map(st => st.id));
    const helpers = allStaff.filter(st => !ownIds.has(st.id) && st.role === 'staff' && st.status !== 'resigned' && (st.positionIds || []).includes(v.positionId || ''));
    const evaluate = (st: Staff, isHelp: boolean) => {
      const req = isHelp ? undefined : periodRequests.find(r => r.staffId === st.id && r.type === 'request' && !r.confirmed && r.start.split('T')[0] === day);
      const off = !isHelp && periodRequests.some(r => r.staffId === st.id && r.type === 'off' && r.start.split('T')[0] === day);
      const assignedSameDay = periodAssignedAll.some(a => a.staffId === st.id && a.start.split('T')[0] === day);
      const canPosition = (st.positionIds || []).includes(v.positionId || '');
      const level = st.skills?.find(sk => sk.positionId === v.positionId)?.level || 0;
      const hours = staffHoursInPeriod(st.id);
      const ros = req ? Math.max(vs, sMin(req)) : vs;
      const roe = req ? Math.min(ve, eMin(req)) : ve;
      const overlap = roe - ros;
      const isRequestFit = !!req && overlap >= Math.min(minShift, ve - vs);
      const fit = isRequestFit ? fitToGaps(ros, roe, gaps, minShift) : gapSpan;
      const gain = gapOverlap(fit.start, fit.end, gaps);
      const addHours = isRequestFit ? (fit.end - fit.start) / 60 : (gapSpan.end - gapSpan.start) / 60 || slotHours;
      const overLimit = !!st.monthlyWorkHourLimit && hours + addHours > st.monthlyWorkHourLimit;
      const consecutive = maxConsecutiveDays(st.id, [...periodAssignedAll, { ...v, staffId: st.id, type: 'fixed' as const }]);
      const applied = (v.applicants || []).includes(st.id);
      const offered = (v.offeredTo || []).includes(st.id);
      const hardReasons: string[] = [];
      if (!canPosition) hardReasons.push('ポジション対象外');
      if (assignedSameDay) hardReasons.push('同日に割当済み');
      if (overLimit) hardReasons.push('月間上限超え');
      if (consecutive >= 6) hardReasons.push(`${consecutive}連勤`);
      // 相性NG・ペアは、実際に入る時間（不足に合わせて詰めた時間）で判定する
      const ws = isRequestFit ? fit.start : gapSpan.start; const we = isRequestFit ? fit.end : gapSpan.end;
      const ng = ngConflicts(st.id, day, ws, we, periodAssignedAll);
      if (ng.length > 0) hardReasons.push(`NG：${ng.join('、')}と同じ時間帯`);
      const pairPartners = pairPartnersOf(st.id);
      const pairMissing = pairPartners.filter(pid => !partnerPresent(pid, day, ws, we, periodAssignedAll)).map(nameOf);
      const assignedShift = alreadyAssigned.find(a => a.staffId === st.id);
      let kind: CandidateKind;
      const reasons = [...hardReasons];
      if (assignedShift) kind = 'assigned';
      else if (hardReasons.length > 0) kind = 'ng';
      else if (applied) kind = 'applicant';
      else if (isRequestFit) kind = 'request';
      else if (off) { kind = 'ng'; reasons.unshift('休み希望'); }
      else if (req) { kind = 'ng'; reasons.unshift('希望の時間帯が合わない'); }
      else kind = isHelp ? 'help' : 'available';
      const useRequest = kind === 'request' || kind === 'assigned';
      return {
        staff: st, kind, request: useRequest ? (req || (assignedShift ? { ...assignedShift } : undefined)) : undefined, assignedShift, level, hours, addHours,
        ok: kind === 'request' || kind === 'applicant' || kind === 'available' || kind === 'help', reasons, isAssignedHere: !!assignedShift, applied, offered, isHelp,
        pairNote: pairPartners.length === 0 ? '' : pairMissing.length === 0 ? `ペア：${pairPartners.map(nameOf).join('、')}と同じ時間帯` : `ペア：${pairMissing.join('、')}がこの時間帯にいません`,
        overlapStart: useRequest && req ? fit.start : gapSpan.start,
        overlapEnd: useRequest && req ? fit.end : gapSpan.end,
        gain,
      };
    };
    const ORDER: Record<CandidateKind, number> = { assigned: 0, applicant: 1, request: 2, available: 3, help: 4, ng: 5 };
    return [
      ...storeStaff.filter(st => st.role !== 'store_manager' && st.role !== 'system_admin').map(st => evaluate(st, false)),
      ...helpers.map(st => evaluate(st, true)),
    ].filter(c => !(c.isHelp && c.kind === 'ng'))
      .sort((a, b) => (ORDER[a.kind] - ORDER[b.kind]) || (b.gain - a.gain) || (PRI[b.staff.priority || 'low'] - PRI[a.staff.priority || 'low']) || (b.level - a.level));
  }, [storeStaff, allStaff, periodRequests, periodAssigned, periodAssignedAll, assignedForVacancy, gapsOf, minShift, staffHoursInPeriod, ngConflicts, pairPartnersOf, partnerPresent, nameOf]);

  // 募集枠ごとの回答者数（この時間帯の希望、または応募）
  const respondentCount = useCallback((v: Shift) => candidatesFor(v).filter(c => c.kind === 'request' || c.kind === 'applicant' || (c.kind === 'assigned' && !!c.request)).length, [candidatesFor]);

  // 打診：希望を出していないスタッフや他店舗のスタッフに、この枠に入れるか個別に確認する
  const handleOffer = async (v: Shift, staffId: string) => {
    const key = `${v.id}:${staffId}`;
    if (offeringKey === key) return; // 二重送信を防ぐ
    setOfferingKey(key);
    try { await api.offerShift(v.id, staffId); }
    catch (e) { showToast('打診の送信に失敗しました。', 'error'); }
    finally { setOfferingKey(null); }
  };

  // 人手が足りない枠（必要人数に対して割り当てが不足）
  const shortageVacancies = useMemo(() => periodVacancies
    .map(v => { const gaps = gapsOf(v); return { v, gaps, missing: maxMissing(gaps) }; })
    .filter(x => x.missing > 0), [periodVacancies, gapsOf]);

  // 募集ステップの「希望の集まり状況」：日ごとに、希望（または応募）を出した人を1人1回だけ、
  // 不足している時間を最も埋められる枠に仮に当てはめ、それでも残る不足を「不足の見込み」とする。
  const requestCoverage = useMemo(() => {
    if (currentStep !== 1) return [] as Array<{ v: Shift; gaps: CoverageGap[] }>;
    const out: Array<{ v: Shift; gaps: CoverageGap[] }> = [];
    const byDay = new Map<string, Shift[]>();
    periodVacancies.forEach(v => { const d = v.start.slice(0, 10); byDay.set(d, [...(byDay.get(d) || []), v]); });
    byDay.forEach((vs, day) => {
      const dayReqs = periodRequests.filter(r => r.type === 'request' && r.start.startsWith(day));
      const sim: Shift[] = periodAssigned.filter(a => a.start.startsWith(day));
      const used = new Set(sim.map(a => a.staffId));
      const intervalOf = (st: Staff, v: Shift) => {
        const s0 = sMin(v); const e0 = eMin(v);
        if ((v.applicants || []).includes(st.id)) return { s: s0, e: e0 };
        const r = dayReqs.find(x => x.staffId === st.id);
        if (!r) return null;
        const s1 = Math.max(s0, sMin(r)); const e1 = Math.min(e0, eMin(r));
        return e1 - s1 >= Math.min(minShift, e0 - s0) ? { s: s1, e: e1 } : null;
      };
      for (;;) {
        let best: { v: Shift; st: Staff; start: number; end: number; g: number } | null = null;
        for (const v of vs) {
          const gaps = vacancyGaps(v, periodVacancies, sim);
          if (gaps.length === 0) continue;
          for (const st of storeStaff) {
            if (used.has(st.id) || !(st.positionIds || []).includes(v.positionId || '')) continue;
            const iv = intervalOf(st, v);
            if (!iv) continue;
            const fit = fitToGaps(iv.s, iv.e, gaps, minShift);
            const g = gapOverlap(fit.start, fit.end, gaps);
            if (g > 0 && (!best || g > best.g)) best = { v, st, start: fit.start, end: fit.end, g };
          }
        }
        if (!best) break;
        used.add(best.st.id);
        sim.push({ id: `sim_${best.st.id}`, staffId: best.st.id, store: best.v.store, positionId: best.v.positionId, start: isoAt(day, best.start), end: isoAt(day, best.end), type: 'fixed', confirmed: true });
      }
      vs.forEach(v => { const gaps = vacancyGaps(v, periodVacancies, sim); if (gaps.length > 0) out.push({ v, gaps }); });
    });
    return out.sort((a, b) => a.v.start.localeCompare(b.v.start));
  }, [currentStep, periodVacancies, periodRequests, periodAssigned, storeStaff, minShift]);

  // 追加募集：不足枠をスタッフに公開して応募を受け付ける
  const handleOpenCall = async (vacancyIds: string[]) => {
    if (vacancyIds.length === 0) return;
    setIsSending(true);
    try {
      await api.openCallVacancies(selectedStoreId, vacancyIds);
    } catch (e) { showToast('追加募集の送信に失敗しました。', 'error'); }
    finally { setIsSending(false); }
  };

  // 割り当て：希望シフトを募集枠の時間に合わせて確定済みにする
  const handleAssign = async (v: Shift, staffId: string) => {
    const c = candidatesFor(v).find(x => x.staff.id === staffId);
    if (!c) return;
    const day = v.start.split('T')[0];
    const start = isoAt(day, c.overlapStart);
    const end = isoAt(day, c.overlapEnd);
    const base: Shift = c.request ? { ...c.request } : { id: `asg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, staffId, store: selectedStoreId, start, end, type: 'request', confirmed: false };
    try {
      await api.saveShifts([{ ...base, requestedStart: base.requestedStart || base.start, requestedEnd: base.requestedEnd || base.end, start, end, positionId: v.positionId, confirmed: true, label: c.request ? (c.request.label || '希望') : c.isHelp ? 'ヘルプ' : c.kind === 'applicant' ? '追加募集' : '手動割当', vacancyId: v.label === '欠員' ? v.id : null }]);
      setIsDirty(true);
    } catch (e) { showToast('割り当てに失敗しました。', 'error'); }
  };

  // 割り当て解除：確定を外して希望に戻す（手動割当は削除）
  const handleUnassign = async (v: Shift, staffId: string) => {
    const target = assignedForVacancy(v).find(a => a.staffId === staffId);
    if (!target) return;
    try {
      if (target.type === 'fixed') { showToast('固定シフトは解除できません。スタッフ設定から変更してください。', 'info'); return; }
      if (target.label === '手動割当' || target.label === '追加募集' || target.label === 'ヘルプ') await api.deleteShift(target.id);
      else await api.saveShifts([{ ...target, start: target.requestedStart || target.start, end: target.requestedEnd || target.end, confirmed: false, vacancyId: null }]);
      setIsDirty(true);
    } catch (e) { showToast('解除に失敗しました。', 'error'); }
  };

  // 自動割り当て（貪欲法）：全募集枠に対し、条件を満たす候補を優先度→レベル順に埋める
  // 希望をもとにシフトを自動作成する（「調整に進む」で自動実行、または「もう一度自動作成」から実行）
  const runAutoAssign = async () => {
    setIsSending(true);
    try {
      let assigned: Shift[] = [...periodAssignedAll];
      const updates: Shift[] = [];
      const PRI = { high: 3, medium: 2, low: 1 } as const;
      const workers = storeStaff.filter(st => st.role !== 'store_manager' && st.role !== 'system_admin');

      // 募集枠に入れられる候補（希望あり・ポジション対象・同日未割当・最短勤務時間以上重なる・月間上限内・6連勤未満・相性NGなし）。
      // 入る時間は、希望と枠の重なりを、まだ足りない時間に合わせて詰める（gain：不足を埋められる時間）。
      const excluded = new Set<string>();
      const eligible = (v: Shift, gaps: CoverageGap[], allowNoGain = false) => {
        const day = v.start.split('T')[0];
        const vs = sMin(v); const ve = eMin(v);
        return workers.map(st => {
          const req = periodRequests.find(r => r.staffId === st.id && r.type === 'request' && !r.confirmed && r.start.split('T')[0] === day);
          if (!req) return null;
          if (!(st.positionIds || []).includes(v.positionId || '')) return null;
          if (assigned.some(a => a.staffId === st.id && a.start.split('T')[0] === day)) return null;
          if (excluded.has(`${day}:${st.id}`)) return null; // ペアの相手が入れず見送った人は、その日は入れない
          const ros = Math.max(vs, sMin(req)); const roe = Math.min(ve, eMin(req));
          if (roe - ros < Math.min(minShift, ve - vs)) return null; // 最短勤務時間に満たない重なりは対象外
          const fit = fitToGaps(ros, roe, gaps, minShift);
          const os = fit.start; const oe = fit.end;
          const gain = gapOverlap(os, oe, gaps);
          if (gain <= 0 && !allowNoGain) return null;
          const hours = staffHoursInPeriod(st.id, assigned) + (oe - os) / 60;
          if (st.monthlyWorkHourLimit && hours > st.monthlyWorkHourLimit) return null;
          if (maxConsecutiveDays(st.id, [...assigned, { ...v, staffId: st.id, type: 'fixed' as const }]) >= 6) return null;
          if (ngConflicts(st.id, day, os, oe, assigned).length > 0) return null; // 相性NGの相手と同じ時間帯にしない
          const level = st.skills?.find(sk => sk.positionId === v.positionId)?.level || 0;
          return { st, req, os, oe, gain, level, pri: PRI[st.priority || 'low'] };
        }).filter((x): x is NonNullable<typeof x> => !!x);
      };

      // 日ごとに、候補が少ない（埋めにくい）枠から順に1名ずつ割り当てる。不足は30分ごとの人数で判定する。
      // 候補者は「毎日必要な業務」「ペア」「同時間帯の上限」を優先し、次に不足を長く埋められる人、他の枠に回せない人、優先度・スキルの高い人を選ぶ。
      const days = Array.from(new Set(periodVacancies.map(v => v.start.split('T')[0]))).sort();
      const fillDay = (day: string) => {
        const dayVacancies = periodVacancies.filter(v => v.start.split('T')[0] === day);
        for (;;) {
          const open = dayVacancies
            .map(v => { const gaps = vacancyGaps(v, periodVacancies, assigned); return { v, gaps, cands: gaps.length > 0 ? eligible(v, gaps) : [] }; })
            .filter(x => x.cands.length > 0);
          if (open.length === 0) break;
          open.sort((a, b) => (a.cands.length - b.cands.length) || ((a.cands.length - maxMissing(a.gaps)) - (b.cands.length - maxMissing(b.gaps))) || a.v.start.localeCompare(b.v.start));
          const { v, cands } = open[0];
          const flexibility = (staffId: string) => open.filter(o => o.cands.some(c => c.st.id === staffId)).length;
          // その日にまだ対応者がいない「毎日必要な業務」をこなせる人を最優先にする。
          const missingDuties = storeDuties.filter(d => d.requiredDaily && dutyHoldersOn(day, d.id, assigned).length === 0);
          const dutyScore = (st: Staff) => missingDuties.filter(d => (st.dutyIds || []).includes(d.id)).length;
          // ペア：相手がすでにいる人、または相手を待っている人を優先する
          const pairScore = (c: { st: Staff; os: number; oe: number }) =>
            pairPartnersOf(c.st.id).filter(pid => partnerPresent(pid, day, c.os, c.oe, assigned)).length
            + storeRules.filter(r => r.type === 'pair' && r.partnerId === c.st.id && assigned.some(a => a.staffId === r.staffId && a.store === selectedStoreId && a.start.startsWith(day) && sMin(a) < c.oe && eMin(a) > c.os)).length;
          // 同じ時間帯の上限がある業務（インチャージなど）は、上限を超える人を後回しにする
          const overPenalty = (c: { st: Staff; os: number; oe: number }) => storeDuties.filter(d => d.maxConcurrent && (c.st.dutyIds || []).includes(d.id))
            .filter(d => assigned.filter(a => a.store === selectedStoreId && a.start.startsWith(day) && staffHasDuty(a.staffId, d.id) && sMin(a) < c.oe && eMin(a) > c.os).length >= (d.maxConcurrent || 0)).length;
          cands.sort((a, b) => (dutyScore(b.st) - dutyScore(a.st)) || (pairScore(b) - pairScore(a)) || (overPenalty(a) - overPenalty(b)) || (b.gain - a.gain) || (flexibility(a.st.id) - flexibility(b.st.id)) || (b.pri - a.pri) || (b.level - a.level));
          const c = cands[0];
          const upd: Shift = { ...c.req, requestedStart: c.req.requestedStart || c.req.start, requestedEnd: c.req.requestedEnd || c.req.end, start: isoAt(day, c.os), end: isoAt(day, c.oe), positionId: v.positionId, confirmed: true, vacancyId: v.label === '欠員' ? v.id : null };
          updates.push(upd); assigned = [...assigned, upd];
        }
      };
      days.forEach(fillDay);
      // ペアの仕上げ：相手のいない人には、相手を同じ時間帯の枠に入れる（相手の希望がある場合）。
      // 入れられない場合は、その人の割り当てを見送る（相手なしで入れない）。
      // 見送った人の時間を埋め直すと別のペアの人が入ることがあるので、変化がなくなるまで繰り返す。
      for (let round = 0, changed = true; changed && round < 5; round++) {
      changed = false;
      for (const r of storeRules.filter(x => x.type === 'pair')) {
        for (const a of updates.filter(u => u.staffId === r.staffId)) {
          const day = a.start.slice(0, 10);
          if (partnerPresent(r.partnerId, day, sMin(a), eMin(a), assigned)) continue;
          changed = true;
          const dayVacancies = periodVacancies.filter(v => v.start.startsWith(day));
          // 相手は同じ時間帯に入ってもらう（枠が埋まっていても、ペアのために追加する）
          const options = dayVacancies.flatMap(v => eligible(v, [], true).filter(c => c.st.id === r.partnerId && c.os < eMin(a) && c.oe > sMin(a)).map(c => ({ v, c, rem: missingMinutes(vacancyGaps(v, periodVacancies, assigned)) })))
            .sort((x, y) => y.rem - x.rem);
          const pick = options[0];
          if (pick) {
            const upd: Shift = { ...pick.c.req, requestedStart: pick.c.req.requestedStart || pick.c.req.start, requestedEnd: pick.c.req.requestedEnd || pick.c.req.end, start: isoAt(day, pick.c.os), end: isoAt(day, pick.c.oe), positionId: pick.v.positionId, confirmed: true, vacancyId: pick.v.label === '欠員' ? pick.v.id : null };
            updates.push(upd); assigned = [...assigned, upd];
          } else {
            const idx = updates.indexOf(a);
            updates.splice(idx, 1);
            assigned = assigned.filter(x => x !== a);
            // 見送った人の時間を、ほかの候補で埋め直す
            excluded.add(`${day}:${a.staffId}`);
            fillDay(day);
          }
        }
      }
      }
      if (updates.length > 0) await api.saveShifts(updates);
      // 結果のまとめ：充足率、不足枠、業務の対応者がいない日
      // 充足率は「必要人数×時間」に対して埋まった割合
      const shortages = periodVacancies.map(v => { const gaps = vacancyGaps(v, periodVacancies, assigned); return { v, gaps, missing: maxMissing(gaps) }; }).filter(x => x.missing > 0).sort((a, b) => a.v.start.localeCompare(b.v.start));
      const totalNeed = periodVacancies.reduce((acc, v) => acc + requiredMinutes(v), 0);
      const missingTotal = shortages.reduce((acc, x) => acc + missingMinutes(x.gaps), 0);
      const dutyMissing: Array<{ day: string; duty: Duty }> = [];
      days.forEach(day => storeDuties.filter(d => d.requiredDaily).forEach(d => { if (dutyHoldersOn(day, d.id, assigned).length === 0) dutyMissing.push({ day, duty: d }); }));
      setAutoResult({ assignedCount: updates.length, totalNeed, filled: totalNeed - missingTotal, shortages, dutyMissing });
      setIsAutoResultOpen(true);
      setIsDirty(true);
    } catch (e) { showToast('自動割り当てに失敗しました。', 'error'); } finally { setIsSending(false); }
  };

  const handleAutoAssign = () => {
    setDialogState({
      isOpen: true, type: 'confirm', title: 'もう一度自動作成',
      message: `回収したシフト希望をもとに、まだ埋まっていない募集枠へスタッフを自動で割り当てます。\n\n条件：担当できるポジション、同じ日に重複しない、月間の勤務上限内、6連勤にならない、相性NGの相手と同じ時間帯にしない。\n人が集まりにくい枠から順に、毎日必要な業務の対応者とペアを優先し、優先度とスキルを考慮して割り当てます。ペアの相手が入れない人は割り当てを見送ります。すでに割り当て済みの枠はそのまま残します。\n\n実行してよろしいですか？`,
      onConfirm: async () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        await runAutoAssign();
      }
    });
  };

  // 催促（未提出者へ）
  const handleRemind = async (staffIds: string[]) => {
    if (staffIds.length === 0) return;
    try { await api.sendShiftReminder(selectedStoreId, staffIds); } catch (e) { showToast('催促の送信に失敗しました。', 'error'); }
  };

  // 期間確定ハンドラ
  const handleConfirmPeriod = async () => {
    if (!setupStartDate || !setupEndDate || !activeStore) return;

    if (setupStartDate < todayStr) {
      showToast('開始日に過去の日付は指定できません。', 'error');
      return;
    }
    if (setupEndDate < setupStartDate) {
      showToast('終了日は開始日以降の日付を指定してください。', 'error');
      return;
    }

    // 期間が変わっていなければ何もしない（進行中のステップや募集枠を保つ）。
    if (activeStore.lastShiftPeriod && activeStore.lastShiftPeriod.start === setupStartDate && activeStore.lastShiftPeriod.end === setupEndDate) {
      setIsPeriodModalOpen(false);
      return;
    }

    setIsLoading(true);
    const start = new Date(setupStartDate);
    const end = new Date(setupEndDate);
    setTargetPeriod({ start, end });
    setCurrentDate(start);

    try {
      const updatedStore = { ...activeStore, lastShiftPeriod: { start: setupStartDate, end: setupEndDate }, shiftCreationStatus: 'planning' as const };
      setCurrentStep(0);
      await api.saveStores([updatedStore]);
      setStores(prev => prev.map(s => s.id === updatedStore.id ? updatedStore : s));

      const newVacancies: Shift[] = [];
      const current = new Date(start);
      while (current <= end) {
        const day = current.getDay();
        const dStr = getJSTDateString(current);
        const matchingPattern = shiftPatterns.find(p => p.defaultDays?.includes(day));
        // 既に募集枠がある日は作り直さない（同じ期間を設定し直しても枠が重複しないように）。
        const alreadyPlanned = (await api.getShifts({ start: dStr, end: dStr }, selectedStoreId)).some(sh => sh.type === 'vacancy');
        if (matchingPattern && !alreadyPlanned) {
          matchingPattern.items.forEach(item => {
            if (!newVacancies.some(v => v.start.startsWith(dStr) && v.positionId === item.positionId)) {
              newVacancies.push({
                id: `vac_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                staffId: null, store: selectedStoreId,
                ...isoRange(dStr, item.startTime, item.endTime), // 終了が開始以前なら翌日（深夜営業）
                type: 'vacancy', positionId: item.positionId,
                headcount: item.headcount, confirmed: false
              });
            }
          });
        }
        current.setDate(current.getDate() + 1);
      }
      if (newVacancies.length > 0) {
        await api.saveShifts(newVacancies);
        showToast(`${newVacancies.length}件の募集枠を自動生成しました。`);
      }
      setIsPeriodModalOpen(false);
    } catch (e) { showToast('期間設定の保存に失敗しました。', 'error'); }
    finally { setIsLoading(false); }
  };

  // 期間リセット
  const handleResetPeriod = () => {
    setDialogState({
      isOpen: true, type: 'confirm', title: '期間設定のリセット',
      message: '設定された作成期間を解除、期間中の募集枠データなどがリセットされます。よろしいですか？',
      onConfirm: async () => {
        if (!activeStore) return;
        setIsLoading(true);
        setDialogState(prev => ({ ...prev, isOpen: false }));
        try {
          const updatedStore = { ...activeStore, lastShiftPeriod: undefined, shiftCreationStatus: 'planning' as const };
          // 期間中の募集枠を削除し、期間設定を解除する。
          if (targetPeriod) {
            const ps = getJSTDateString(targetPeriod.start); const pe = getJSTDateString(targetPeriod.end);
            const periodVacancyIds = (await api.getShifts({ start: ps, end: pe }, selectedStoreId)).filter(sh => sh.type === 'vacancy').map(sh => sh.id);
            await Promise.all(periodVacancyIds.map(id => api.deleteShift(id)));
          }
          await api.clearShiftPeriod(activeStore.id);
          setCurrentStep(0);
          setStores(prev => prev.map(s => s.id === updatedStore.id ? updatedStore : s));
          setTargetPeriod(null);
          setSetupStartDate('');
          setSetupEndDate('');
          setIsPeriodModalOpen(true);
          showToast('期間設定を解除しました。');
        } catch (e) {
          showToast('解除に失敗しました。', 'error');
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  // ドラッグ操作
  const handleMouseDown = (positionId: string, slotIndex: number) => {
    if (currentStep !== 0 || isTodayHoliday) return;
    if (vacancies.some(v => v.positionId === positionId)) {
      showToast('1つのポジションにつき作成できる募集枠は1つまでです。', 'info');
      return;
    }
    setCreateDrag({ isDragging: true, positionId, startSlot: slotIndex, currentSlot: slotIndex });
    setIsDirty(true);
  };

  const handleMouseEnter = (positionId: string, slotIndex: number) => {
    if (createDrag.isDragging) setCreateDrag(prev => ({ ...prev, currentSlot: slotIndex }));
    else if (editDrag.mode) setEditDrag(prev => ({ ...prev, currentSlot: slotIndex }));
  };

  const handleMouseUp = async () => {
    if (createDrag.isDragging && createDrag.startSlot !== null && createDrag.currentSlot !== null) {
      const startSlot = Math.min(createDrag.startSlot, createDrag.currentSlot);
      const endSlot = Math.max(createDrag.startSlot, createDrag.currentSlot) + 1;
      const dateStr = getJSTDateString(currentDate);

      const newVacancy: Shift = {
        id: `vac_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        staffId: null, store: selectedStoreId,
        start: isoAt(dateStr, startSlot * 30), end: isoAt(dateStr, endSlot * 30),
        type: 'vacancy', positionId: createDrag.positionId!, headcount: 1, confirmed: false
      };
      try { await api.saveShifts([newVacancy]); } catch (e) { showToast('作成に失敗しました。', 'error'); }
    } else if (editDrag.mode && editDrag.targetId) {
      if (Math.abs(editDrag.currentSlot - editDrag.initialSlot) === 0) {
        const vacancy = vacancies.find(v => v.id === editDrag.targetId);
        if (vacancy) {
          if (currentStep >= 2) { setSelectedVacancyForCandidates(vacancy); setIsCandidateModalOpen(true); }
          else if (currentStep === 0) { setEditingVacancy(vacancy); setIsEditModalOpen(true); }
        }
      } else {
        const diff = (editDrag.currentSlot - editDrag.initialSlot) * 30;
        const vacancy = vacancies.find(v => v.id === editDrag.targetId);
        if (vacancy) {
          let newStartMin = editDrag.initialStartMinutes;
          let newEndMin = editDrag.initialEndMinutes;
          if (editDrag.mode === 'move') { newStartMin += diff; newEndMin += diff; }
          else if (editDrag.mode === 'resize-start') { newStartMin += diff; }
          else if (editDrag.mode === 'resize-end') { newEndMin += diff; }

          // 開始はその日のうち（0:00〜23:30）、長さは24時間未満にする（翌日の営業日に移らないように）
          if (editDrag.mode === 'move') {
            if (newStartMin < 0) { newEndMin -= newStartMin; newStartMin = 0; }
            if (newStartMin > 1410) { newEndMin -= newStartMin - 1410; newStartMin = 1410; }
          } else newStartMin = Math.max(0, Math.min(1410, newStartMin));
          newEndMin = Math.min(newEndMin, newStartMin + 1440 - 30);
          if (newEndMin > newStartMin) {
            const dateStr = getJSTDateString(currentDate);
            const updated = {
              ...vacancy,
              start: isoAt(dateStr, newStartMin),
              end: isoAt(dateStr, newEndMin)
            };
            try { await api.saveShifts([updated]); setIsDirty(true); } catch (e) { showToast('更新に失敗しました。', 'error'); }
          }
        }
      }
    }
    setCreateDrag(INITIAL_CREATE_DRAG);
    setEditDrag(INITIAL_EDIT_DRAG);
  };

  const handleEditDragStart = (e: React.MouseEvent, mode: 'move' | 'resize-start' | 'resize-end', vacancy: Shift, clickedSlot: number) => {
    if (currentStep !== 0 || isTodayHoliday) return;
    e.stopPropagation();
    const startMin = sMin(vacancy);
    const endMin = eMin(vacancy);
    const startSlot = Math.floor(startMin / 30);
    const offset = clickedSlot - startSlot;
    setEditDrag({ mode, targetId: vacancy.id, initialStartMinutes: startMin, initialEndMinutes: endMin, initialSlot: clickedSlot, currentSlot: clickedSlot, offsetSlots: offset });
  };

  // 募集枠操作
  const handleSaveVacancyDetail = async () => {
    if (!editingVacancy.id || !editingVacancy.start || !editingVacancy.end) return;
    if (editingVacancy.start.slice(11, 16) === editingVacancy.end.slice(11, 16)) { showToast('開始時刻と終了時刻が同じです。', 'error'); return; }
    try {
      // 終了が開始以前なら翌日の終了として保存する（深夜営業）
      const day = editingVacancy.start.slice(0, 10);
      await api.saveShifts([{ ...(editingVacancy as Shift), ...isoRange(day, editingVacancy.start.slice(11, 16), editingVacancy.end.slice(11, 16)) }]);
      setIsEditModalOpen(false);
      showToast('枠情報を更新しました。');
      setIsDirty(true);
    } catch (e) { showToast('保存に失敗しました。', 'error'); }
  };

  const handleDeleteVacancy = (vacancy?: Shift) => {
    const target = vacancy || (editingVacancy as Shift);
    if (!target.id) return;
    setDialogState({
      isOpen: true, type: 'confirm', title: '募集枠の削除', message: 'この募集枠を削除しますか？',
      onConfirm: async () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        try {
          await api.deleteShift(target.id!);
          setIsEditModalOpen(false);
          showToast('削除しました。');
          setIsDirty(true);
        } catch (e) { showToast('削除に失敗しました。', 'error'); }
      }
    });
  };

  const handleBatchDelete = () => {
    if (vacancies.length === 0) return;
    setDialogState({
      isOpen: true, type: 'confirm', title: '募集枠の一括削除', message: `${vacancies.length}件の募集枠を削除します。\nこの操作は取り消せません。よろしいですか？`,
      onConfirm: async () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        setIsSending(true);
        try {
          await Promise.all(vacancies.map(v => api.deleteShift(v.id)));
          showToast('募集枠をすべて削除しました。');
          setIsDirty(true);
        } catch (e) { showToast('削除に失敗しました。', 'error'); } finally { setIsSending(false); }
      }
    });
  };

  // パターン保存
  const handleOpenSavePattern = () => {
    if (vacancies.length === 0) {
      showToast('保存する募集枠がありません。', 'info');
      return;
    }
    const dStr = `${currentDate.getMonth()+1}/${currentDate.getDate()}`;
    setNewPatternName(`${dStr}のモデル`);
    setIsSavePatternModalOpen(true);
  };

  const handleSaveAsPattern = async () => {
    if (!newPatternName.trim() || !selectedStoreId) return;
    setIsLoading(true);
    try {
      const patternItems = vacancies.map(v => ({
        positionId: v.positionId!,
        startTime: v.start.split('T')[1].slice(0, 5),
        endTime: v.end.split('T')[1].slice(0, 5),
        headcount: v.headcount || 1
      }));
      const newPattern: ShiftPattern = {
        id: `pattern_${Date.now()}`, storeId: selectedStoreId, name: newPatternName,
        items: patternItems, defaultDays: []
      };
      await api.saveShiftPattern(newPattern);
      showToast('モデルシフトとして保存しました。', 'success');
      setIsSavePatternModalOpen(false);
      await loadInitialData(selectedStoreId);
    } catch (e) {
      showToast('モデルシフトの保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemporarySave = () => { setIsDirty(false); showToast('一時保存しました。', 'success'); };

  // 休日切り替え
  const handleToggleHoliday = async () => {
    const dateStr = getJSTDateString(currentDate);
    const existing = events.find(e => e.date === dateStr && e.title === '休日');

    if (existing) {
      try {
        await api.deleteEvent(existing.id);
        setEvents(prev => prev.filter(e => e.id !== existing.id));
        showToast('営業日に戻しました。');
        setIsDirty(true);
      } catch (e) { showToast('処理に失敗しました。', 'error'); }
    } else {
      setIsRefreshing(true);
      try {
        if (vacancies.length > 0) await Promise.all(vacancies.map(v => api.deleteShift(v.id)));
        const holidayEvent: DailyEvent = { id: `holiday_${Date.now()}`, storeId: selectedStoreId, date: dateStr, title: '休日', isAllDay: true };
        await api.saveEvent(holidayEvent);
        setEvents(prev => [...prev, holidayEvent]);
        showToast('休日として設定しました。');
        setIsDirty(true);
      } catch (e) { showToast('処理に失敗しました。', 'error'); }
      finally { setIsRefreshing(false); }
    }
  };

  // 日付ナビゲーション
  // 割り当て・枠の変更はその場で保存されるので、日付を移動しても失われない（確認は出さない）
  const handleDayNavigation = (direction: 'prev' | 'next') => {
    if (direction === 'prev') handlePrevDay();
    else handleNextDay();
  };

  const handlePrevDay = () => {
    if (!targetPeriod) return;
    const n = new Date(currentDate); n.setDate(n.getDate() - 1);
    if (n < targetPeriod.start) return;
    setCurrentDate(n);
  };

  const handleNextDay = () => {
    if (!targetPeriod) return;
    const n = new Date(currentDate); n.setDate(n.getDate() + 1);
    if (n > targetPeriod.end) return;
    setCurrentDate(n);
  };

  // 行事操作
  const handleSaveEvent = async () => {
    if (!editingEvent.title?.trim() || !editingEvent.storeId) {
      showToast('行事名を入力してください。', 'error');
      return;
    }
    setIsEventSaving(true);
    try {
      await api.saveEvent(editingEvent as DailyEvent);
      showToast('行事を保存しました。', 'success');
      setIsEventModalOpen(false);
      const newEvents = await api.getEvents(selectedStoreId);
      setEvents(newEvents);
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsEventSaving(false);
    }
  };

  const handleDeleteEvent = () => {
    if (!editingEvent.id) return;
    setDialogState({
      isOpen: true, type: 'confirm', title: '行事の削除', message: `「${editingEvent.title}」を削除しますか？`,
      onConfirm: async () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        try {
          await api.deleteEvent(editingEvent.id!);
          showToast('削除しました。', 'success');
          setIsEventModalOpen(false);
          const newEvents = await api.getEvents(selectedStoreId);
          setEvents(newEvents);
        } catch (e) {
          showToast('削除に失敗しました。', 'error');
        }
      }
    });
  };

  // シフト回答依頼送信
  const handleSendRequest = async () => {
    setDialogState({
      isOpen: true, type: 'confirm', title: 'シフト回答依頼の送信',
      message: `指定された期間（${formatPeriodJP(targetPeriod!)}）の募集枠に基づいて、スタッフに${getNotificationChannelLabel(activeStore)}でシフト提出依頼を送ります。よろしいですか？`,
      onConfirm: async () => {
        setIsSending(true);
        try {
          await api.publishShifts({ storeId: selectedStoreId, period: { start: getJSTDateString(targetPeriod!.start), end: getJSTDateString(targetPeriod!.end) } });
          setCurrentStep(1); setIsDirty(false); setDialogState(prev => ({...prev, isOpen: false}));
          await persistStatus('requesting');
          showToast('依頼を送信しました。', 'success');
        } catch(e) { showToast('送信に失敗しました。', 'error'); } finally { setIsSending(false); }
      }
    });
  };

  // シフト確定・通知送信
  const handleConfirmShifts = async () => {
    setDialogState({
      isOpen: true, type: 'confirm', title: 'シフトの確定',
      message: `指定された期間（${formatPeriodJP(targetPeriod!)}）のシフトを確定し、スタッフに${getNotificationChannelLabel(activeStore)}で通知を送ります。よろしいですか？`,
      onConfirm: async () => {
        setIsSending(true);
        try {
          await api.confirmShifts(selectedStoreId, { start: getJSTDateString(targetPeriod!.start), end: getJSTDateString(targetPeriod!.end) });
          setDialogState(prev => ({...prev, isOpen: false}));
          await persistStatus('published');
          showToast('シフトを確定し、通知を送信しました。', 'success');
        } catch(e) { showToast('確定処理に失敗しました。', 'error'); } finally { setIsSending(false); }
      }
    });
  };

  // 日付フォーマット
  const formatDateJP = (d: Date) => `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日（${['日','月','火','水','木','金','土'][d.getDay()]}）`;
  const formatPeriodJP = (p: {start: Date, end: Date}) => `${p.start.getMonth()+1}/${p.start.getDate()} 〜 ${p.end.getMonth()+1}/${p.end.getDate()}`;

  return {
    // 状態
    currentDate, stores, positions, allStaff, vacancies, requests, assignedShifts,
    events, selectedStoreId, isLoading, isRefreshing, isSending, currentStep,
    targetPeriod, isDirty, isPeriodModalOpen, setupStartDate, setupEndDate,
    createDrag, editDrag, isEditModalOpen, editingVacancy, isCandidateModalOpen,
    selectedVacancyForCandidates, isEventModalOpen, editingEvent, isEventSaving,
    isSavePatternModalOpen, newPatternName, dialogState, todayStr, activeStore,
    unconfiguredDaysCount, isTodayHoliday, isReadyForRequest,
    periodDays, storeStaff, periodAssigned, periodVacancies, periodRequests, unsubmittedStaff, warnings, duties, storeDuties, dayDutyStatus,
    assignedForVacancy, candidatesFor, staffHoursInPeriod,
    // セッター
    setIsPeriodModalOpen, setSetupStartDate, setSetupEndDate, setIsEditModalOpen,
    setEditingVacancy, setIsCandidateModalOpen, setSelectedVacancyForCandidates,
    setIsEventModalOpen, setEditingEvent, setIsSavePatternModalOpen, setNewPatternName,
    setDialogState,
    // ハンドラ
    navigate, handleConfirmPeriod, handleResetPeriod, handleMouseDown, handleMouseEnter,
    handleMouseUp, handleEditDragStart, handleSaveVacancyDetail, handleDeleteVacancy,
    handleBatchDelete, handleOpenSavePattern, handleSaveAsPattern, handleTemporarySave,
    handleToggleHoliday, handleDayNavigation, handleSaveEvent, handleDeleteEvent,
    handleSendRequest, handleConfirmShifts, formatDateJP, formatPeriodJP,
    goToStep, gapsOf, minShift, respondentCount, activeApplicantCount, isAutoResultOpen, setIsAutoResultOpen, offeringKey, handleChangeStore, addPosition, removePosition, handleOffer, handleOpenCall, shortageVacancies, autoResult, setAutoResult, requestCoverage, handleAssign, handleUnassign, handleAutoAssign, handleRemind, setCurrentDate,
  };
};
